import React, { useState, useEffect } from 'react';
import { LoginAuditEntry, ApiResponse, ManagedUser } from '../../types';
import { settingsService, SearchParams } from '../../services/api';
import { formatDateTime } from '../../utils/datetime';

const LoginAuditTable: React.FC = () => {
  const [entries, setEntries] = useState<LoginAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    page: 1,
    limit: 25,
    search: '',
    sort_by: 'attempted_at',
    sort_order: 'DESC',
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_count: 0,
    limit: 25,
    has_next: false,
    has_prev: false,
  });

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [doneIds, setDoneIds] = useState<Set<number>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [creatingForId, setCreatingForId] = useState<number | null>(null);
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newIpChoice, setNewIpChoice] = useState<'this_ip' | 'anywhere'>('this_ip');

  const fetchEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: ApiResponse<LoginAuditEntry> = await settingsService.listLoginAudit(searchParams);
      setEntries(response.entries || []);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to fetch login audit log');
      console.error('Error fetching login audit log:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsers(await settingsService.listUsers());
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const search = new FormData(e.currentTarget).get('search') as string;
    setSearchParams(prev => ({ ...prev, search: search.trim(), page: 1 }));
  };

  const handleSort = (field: string) => {
    setSearchParams(prev => ({
      ...prev,
      sort_by: field,
      sort_order: prev.sort_by === field && prev.sort_order === 'ASC' ? 'DESC' : 'ASC',
      page: 1,
    }));
  };

  const handleAllowIp = async (entry: LoginAuditEntry) => {
    setActionError(null);
    const user = users.find(u => u.email === entry.email);
    if (!user) {
      setActionError(`No account found for ${entry.email}`);
      return;
    }
    setBusyId(entry.id);
    try {
      const merged = Array.from(new Set([...user.allowed_login_ips, entry.source_ip]));
      await settingsService.updateUser({ id: user.id, allowed_login_ips: merged });
      setDoneIds(prev => new Set(prev).add(entry.id));
      fetchUsers();
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Failed to update account');
    } finally {
      setBusyId(null);
    }
  };

  const startCreateAccount = (entry: LoginAuditEntry) => {
    setCreatingForId(entry.id);
    setNewName(entry.email.includes('@') ? entry.email.split('@')[0] : entry.email);
    setNewPassword('');
    setNewIpChoice('this_ip');
    setActionError(null);
  };

  const cancelCreateAccount = () => {
    setCreatingForId(null);
    setNewName('');
    setNewPassword('');
  };

  const saveNewAccount = async (entry: LoginAuditEntry) => {
    setActionError(null);
    setBusyId(entry.id);
    try {
      await settingsService.createUser({
        email: entry.email,
        name: newName.trim(),
        password: newPassword,
        allowed_login_ips: newIpChoice === 'this_ip' ? [entry.source_ip] : [],
      });
      setDoneIds(prev => new Set(prev).add(entry.id));
      cancelCreateAccount();
      fetchUsers();
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Failed to create account');
    } finally {
      setBusyId(null);
    }
  };

  const outcomeClass: Record<string, string> = {
    success: 'status-completed',
    bad_password: 'status-failed',
    ip_blocked: 'status-failed',
    unknown_user: 'status-failed',
  };

  if (loading) return <div className="loading">Loading login audit log...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="settings-panel">
      <h3>Login audit log</h3>

      <form onSubmit={handleSearch} className="search-form">
        <input name="search" type="text" placeholder="Search by email, IP, or outcome..." defaultValue={searchParams.search} />
        <button type="submit">Search</button>
        <button type="button" onClick={() => setSearchParams(prev => ({ ...prev, search: '', page: 1 }))}>Clear</button>
      </form>

      {actionError && <div className="error">{actionError}</div>}

      <div className="pagination-info">
        Showing {entries.length} of {pagination.total_count} attempts
      </div>

      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('attempted_at')} className="sortable">
                When {searchParams.sort_by === 'attempted_at' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('email')} className="sortable">
                Email {searchParams.sort_by === 'email' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('source_ip')} className="sortable">
                Source IP {searchParams.sort_by === 'source_ip' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('outcome')} className="sortable">
                Outcome {searchParams.sort_by === 'outcome' && (searchParams.sort_order === 'ASC' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={5} className="no-data">No login attempts recorded</td></tr>
            ) : (
              entries.map(entry => (
                <React.Fragment key={entry.id}>
                  <tr>
                    <td>{formatDateTime(entry.attempted_at)}</td>
                    <td className="monospace">{entry.email}</td>
                    <td className="monospace">{entry.source_ip}</td>
                    <td>
                      <span className={`status ${outcomeClass[entry.outcome] || ''}`}>{entry.outcome}</span>
                    </td>
                    <td>
                      {doneIds.has(entry.id) ? (
                        <span className="status status-completed">Done</span>
                      ) : entry.outcome === 'ip_blocked' && users.some(u => u.email === entry.email) ? (
                        <button type="button" disabled={busyId === entry.id} onClick={() => handleAllowIp(entry)}>
                          {busyId === entry.id ? 'Allowing…' : 'Allow this IP'}
                        </button>
                      ) : entry.outcome === 'unknown_user' || entry.outcome === 'ip_blocked' ? (
                        creatingForId === entry.id ? null : (
                          <button type="button" onClick={() => startCreateAccount(entry)}>Create account</button>
                        )
                      ) : null}
                    </td>
                  </tr>
                  {creatingForId === entry.id && (
                    <tr>
                      <td colSpan={5}>
                        <div className="inline-action-form">
                          <span className="monospace small">{entry.email} · {entry.source_ip}</span>
                          <input type="text" placeholder="Name" value={newName} onChange={e => setNewName(e.target.value)} />
                          <input type="password" placeholder="Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                          <label className="radio-option">
                            <input
                              type="radio"
                              name={`ip-choice-${entry.id}`}
                              checked={newIpChoice === 'this_ip'}
                              onChange={() => setNewIpChoice('this_ip')}
                            />
                            Restrict to {entry.source_ip}
                          </label>
                          <label className="radio-option">
                            <input
                              type="radio"
                              name={`ip-choice-${entry.id}`}
                              checked={newIpChoice === 'anywhere'}
                              onChange={() => setNewIpChoice('anywhere')}
                            />
                            Allow from anywhere
                          </label>
                          <button type="button" disabled={busyId === entry.id || !newName || !newPassword} onClick={() => saveNewAccount(entry)}>
                            {busyId === entry.id ? 'Creating…' : 'Save'}
                          </button>
                          <button type="button" onClick={cancelCreateAccount}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination.total_pages > 1 && (
        <div className="pagination">
          <button disabled={!pagination.has_prev} onClick={() => setSearchParams(prev => ({ ...prev, page: pagination.current_page - 1 }))}>Previous</button>
          <span className="page-info">Page {pagination.current_page} of {pagination.total_pages}</span>
          <button disabled={!pagination.has_next} onClick={() => setSearchParams(prev => ({ ...prev, page: pagination.current_page + 1 }))}>Next</button>
        </div>
      )}
    </div>
  );
};

export default LoginAuditTable;
